"use client";

import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { RiDownload2Line, RiFocus3Line, RiLoader4Line } from "react-icons/ri";
import { MarketingConsentCard } from "@/components/photo-studio/marketing/marketing-consent-card";
import { MarketingExportPreview, panelSize } from "@/components/photo-studio/marketing/marketing-export-preview";
import { Button, Input, Label, Switch } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PillSelect from "@/components/ui/pill-select";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { type FacePose, detectPose, getFaceLandmarker, loadDetectableImage } from "@/lib/face-align";
import { parseError, readErrorDetail } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { alignPair } from "@/lib/photo-studio/align-pair";
import { ensureStudioMask } from "@/lib/photo-studio/studio-photo";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import type { AttachmentDayPage, NoteAttachment } from "@/types/notes";
import { MARKETING_CONSENT_REQUIRED, type MarketingExport, type MarketingExportLayout, type MarketingExportSize, STUDIO_BACKDROP_LABELS, type StudioBackdrop } from "@/types/photo-studio";
import { downloadFromSignedUrl } from "@/api/auth/gcs";
import { getMarketingExportLink, marketingQueryKeys, marketingQueryOptions, useCreateMarketingExportMutation } from "@/api/provider/photo-studio/marketing";
import { getPhotoStudioSettings, getStudioRenders } from "@/api/provider/photo-studio/studio";
import { photoStudioQueryOptions } from "@/api/provider/photo-studio/studio-query";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";

// TRANSPARENT is not offered: a marketing image needs a solid background.
const EXPORT_BACKDROPS: StudioBackdrop[] = ["WHITE", "BLACK", "GREY", "BRAND", "BLUR", "NONE"];
const EXPORT_SIZES: MarketingExportSize[] = ["PORTRAIT", "STORY", "SQUARE"];
const SIZE_LABELS: Record<MarketingExportSize, string> = {
  PORTRAIT: "Post 4:5",
  STORY: "Story 9:16",
  SQUARE: "Square"
};
const EXPORT_LAYOUTS: MarketingExportLayout[] = ["SIDE_BY_SIDE", "STACKED"];
const LAYOUT_LABELS: Record<MarketingExportLayout, string> = {
  SIDE_BY_SIDE: "Side by side",
  STACKED: "Stacked"
};
const STUDIO_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];
const isStudioImage = (attachment: NoteAttachment) => {
  const path = (attachment.gcsPath ?? "").toLowerCase().split("?")[0];
  return STUDIO_IMAGE_EXTENSIONS.some(ext => path.endsWith(ext));
};
type Slot = "before" | "after";
interface Detected {
  pose: FacePose | null;
  width: number;
  height: number;
}
async function detectFace(url: string): Promise<Detected | null> {
  const landmarker = await getFaceLandmarker();
  if (!landmarker) return null;
  const image = await loadDetectableImage(url);
  return {
    pose: detectPose(landmarker, image, false),
    width: image.naturalWidth,
    height: image.naturalHeight
  };
}
interface Props {
  patientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialBeforeId?: string | null;
  initialAfterId?: string | null;
}
export function MarketingExportDialog({
  patientId,
  open,
  onOpenChange,
  initialBeforeId = null,
  initialAfterId = null
}: Props) {
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const queryClient = useQueryClient();
  // Making a cutout saves it on the photo, which needs note-write access.
  const canMakeCutouts = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  const [preparingCutouts, setPreparingCutouts] = useState(false);
  const {
    data: consent
  } = useQuery({
    ...marketingQueryOptions.consent(patientId),
    enabled: open
  });
  const {
    data: settings
  } = useQuery({
    queryKey: ["photo-studio", "settings"],
    queryFn: getPhotoStudioSettings,
    enabled: open
  });
  const photosQuery = noteQueryOptions.attachmentsInfinite(patientId);
  const {
    data: photoPages,
    isLoading: loadingPhotos,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    ...photosQuery,
    initialPageParam: undefined as string | undefined,
    enabled: open
  });
  const photos = useMemo(() => ((photoPages?.pages ?? []) as (AttachmentDayPage | null)[]).flatMap(page => page?.data ?? []).filter(isStudioImage), [photoPages]);
  const [beforeId, setBeforeId] = useState<string | null>(initialBeforeId);
  const [afterId, setAfterId] = useState<string | null>(initialAfterId);
  const [activeSlot, setActiveSlot] = useState<Slot>(initialBeforeId && !initialAfterId ? "after" : "before");
  const [backdrop, setBackdrop] = useState<StudioBackdrop>("WHITE");
  const [size, setSize] = useState<MarketingExportSize>("PORTRAIT");
  const [layout, setLayout] = useState<MarketingExportLayout>("SIDE_BY_SIDE");
  const [labels, setLabels] = useState({
    before: "Before",
    after: "After"
  });
  const [showLogo, setShowLogo] = useState<boolean | null>(null);
  const [watermarkText, setWatermarkText] = useState<string | null>(null);
  const [disclaimerText, setDisclaimerText] = useState<string | null>(null);
  const [faces, setFaces] = useState<{
    before: Detected | null;
    after: Detected | null;
  } | null>(null);
  const [aligning, setAligning] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [result, setResult] = useState<MarketingExport | null>(null);
  useEffect(() => {
    if (!open) return;
    setBeforeId(initialBeforeId);
    setAfterId(initialAfterId);
    setResult(null);
    setConsentError(false);
    setFaces(null);
  }, [open, initialBeforeId, initialAfterId]);

  // A new photo invalidates the alignment computed for the old one.
  useEffect(() => {
    setFaces(null);
  }, [beforeId, afterId]);
  const before = photos.find(photo => photo.id === beforeId) ?? null;
  const after = photos.find(photo_0 => photo_0.id === afterId) ?? null;

  // A photo uploaded from a file, or taken before Studio, has no cutout yet, so
  // the server would export it on its own background. Make the cutout on this
  // device first, as the photo viewer and the Compare window do.
  useEffect(() => {
    if (!open || backdrop === "NONE" || !canMakeCutouts) return;
    const ids = [beforeId, afterId].filter((id): id is string => !!id);
    if (ids.length === 0) return;
    let cancelled = false;
    setPreparingCutouts(true);
    void (async () => {
      try {
        const found = await Promise.all(ids.map(async id_0 => {
          const state = await queryClient.fetchQuery(photoStudioQueryOptions.attachment(id_0));
          return state.hasMask || (await ensureStudioMask(state));
        }));
        if (cancelled) return;
        if (found.some(ok => !ok)) {
          toast({
            variant: "warning",
            title: "No one found in a photo",
            description: "That photo keeps its own background, because Studio needs a person in it to replace the background."
          });
        }
        await queryClient.invalidateQueries({
          queryKey: ["photo-studio", "renders", "THUMB"]
        });
      } catch (error) {
        console.error("Failed to prepare Studio cutouts:", error);
      } finally {
        if (!cancelled) setPreparingCutouts(false);
      }
    })();
    return () => {
      cancelled = true;
      setPreparingCutouts(false);
    };
  }, [open, backdrop, beforeId, afterId, canMakeCutouts, queryClient]);
  const {
    data: renders
  } = useQuery({
    queryKey: ["photo-studio", "renders", "THUMB", backdrop, beforeId, afterId],
    queryFn: () => getStudioRenders({
      attachmentIds: [beforeId, afterId].filter(Boolean) as string[],
      size: "THUMB",
      backdrop
    }),
    enabled: open && backdrop !== "NONE" && !preparingCutouts && !!(beforeId || afterId)
  });
  // The full photo, not its thumbnail: thumbnails are signed without checking
  // they exist, and a new upload has none yet.
  const previewUrl = (photo_1: NoteAttachment | null) => {
    if (!photo_1) return null;
    const render = renders?.find(r => r.attachmentId === photo_1.id);
    return render?.signedUrl ?? photo_1.signedUrl ?? photo_1.url ?? photo_1.thumbnailSignedUrl ?? null;
  };
  const panel = panelSize(size, layout);
  const alignment = useMemo(() => {
    if (!faces?.before || !faces.after) return null;
    return alignPair(faces.before.pose, faces.before, faces.after.pose, faces.after, panel, "cover");
  }, [faces, panel.width, panel.height]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveShowLogo = showLogo ?? settings?.exportShowLogo ?? true;
  const effectiveWatermark = watermarkText ?? settings?.exportWatermarkText ?? "";
  const effectiveDisclaimer = disclaimerText ?? settings?.exportDisclaimerText ?? "Results may vary.";
  const brandColor = settings?.effectiveBrandColor ?? tenant?.brandPrimaryColor ?? "#4a5d4e";
  const autoAlign = async () => {
    if (!before || !after) return;
    setAligning(true);
    try {
      const urls = [before, after].map(photo_2 => photo_2.signedUrl ?? photo_2.url ?? "");
      const [beforeFace, afterFace] = await Promise.all(urls.map(url => url ? detectFace(url) : Promise.resolve(null)));
      if (!beforeFace?.pose || !afterFace?.pose) {
        toast({
          variant: "warning",
          title: "Could not find a face in both photos",
          description: "The photos will be exported as they are framed."
        });
        setFaces(null);
        return;
      }
      setFaces({
        before: beforeFace,
        after: afterFace
      });
    } catch (error_0) {
      toast({
        variant: "error",
        title: "Auto-align failed",
        description: await parseError(error_0)
      });
    } finally {
      setAligning(false);
    }
  };
  const createExport = useCreateMarketingExportMutation();
  const consented = consent?.consented === true;
  const canExport = consented && !!beforeId && !!afterId && beforeId !== afterId && !preparingCutouts && !createExport.isPending;

  // The server says consent was withdrawn (on the profile or a form), so the
  // card above must stop showing the cached "agreed".
  const refreshConsent = () => void queryClient.invalidateQueries({
    queryKey: marketingQueryKeys.consent(patientId)
  });
  const runExport = async () => {
    if (!beforeId || !afterId) return;
    setConsentError(false);
    try {
      const exported = await createExport.mutateAsync({
        patientId,
        beforeAttachmentId: beforeId,
        afterAttachmentId: afterId,
        backdrop,
        size,
        layout,
        alignment,
        labels,
        showLogo,
        watermarkText,
        disclaimerText
      });
      setResult(exported);
    } catch (error_1) {
      const detail = await readErrorDetail(error_1);
      if (detail === MARKETING_CONSENT_REQUIRED) {
        setConsentError(true);
        refreshConsent();
        return;
      }
      toast({
        variant: "error",
        title: "Could not create the image",
        description: await parseError(error_1)
      });
    }
  };

  // Export links last a few minutes, so a link is signed fresh (and consent
  // re-checked) for every download.
  const freshLink = async (): Promise<string | null> => {
    if (!result) return null;
    try {
      const fresh = await getMarketingExportLink(result.id, {
        download: true
      });
      return fresh?.signedUrl ?? null;
    } catch (error_2) {
      if ((await readErrorDetail(error_2)) === MARKETING_CONSENT_REQUIRED) {
        setConsentError(true);
        refreshConsent();
        setResult(null);
        return null;
      }
      toast({
        variant: "error",
        title: "Could not get a link to the image",
        description: await parseError(error_2)
      });
      return null;
    }
  };
  const download = async () => {
    const url_0 = await freshLink();
    if (url_0) await downloadFromSignedUrl(url_0, `before-after-${result?.id}.jpg`);
  };
  const pick = (photo_3: NoteAttachment) => {
    if (activeSlot === "before") {
      setBeforeId(photo_3.id);
      if (!afterId) setActiveSlot("after");
    } else {
      setAfterId(photo_3.id);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Before and after for marketing</DialogTitle>
          <p className="text-caption text-foreground-secondary">
            Only the background is changed. The patient&apos;s photos are used
            exactly as taken.
          </p>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
          <MarketingConsentCard patientId={patientId} />
          {consentError && <p className="text-caption text-status-error-text">
              This patient has not agreed to their photos being used in
              marketing. Record their consent above before creating the image.
            </p>}

          {result ? <div className="flex flex-col items-center gap-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={result.signedUrl} alt="Before and after" className="max-h-[50vh] rounded-md border object-contain" />
              <div className="flex gap-2">
                <Button variant="primary" onClick={() => void download()}>
                  <RiDownload2Line className="mr-1 size-4" />
                  Download
                </Button>
                <Button variant="ghost" onClick={() => setResult(null)}>
                  Make another
                </Button>
              </div>
            </div> : <div className="grid gap-4 md:grid-cols-[1fr_auto]">
              <div className="min-w-0 space-y-4">
                <div className="space-y-2">
                  <div className="flex gap-2">
                    {(["before", "after"] as Slot[]).map(slot => <Button key={slot} size="sm" variant={activeSlot === slot ? "primary" : "secondary"} onClick={() => setActiveSlot(slot)}>
                        {slot === "before" ? "Pick before" : "Pick after"}
                      </Button>)}
                  </div>
                  {loadingPhotos ? <p className="text-caption text-foreground-muted">
                      Loading photos...
                    </p> : photos.length === 0 ? <p className="text-caption text-foreground-muted">
                      This patient has no photos yet.
                    </p> : <div className="grid max-h-56 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
                      {photos.map(photo_4 => {
                  const role = photo_4.id === beforeId ? "Before" : photo_4.id === afterId ? "After" : null;
                  return <button key={photo_4.id} type="button" onClick={() => pick(photo_4)} className={cx("relative aspect-square overflow-hidden rounded-md border-2", role ? "border-primary" : "border-transparent")}>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={photo_4.thumbnailSignedUrl ?? photo_4.signedUrl ?? ""} alt={photo_4.name ?? "Photo"} className="size-full object-cover"
                    // A new upload has no thumbnail yet: show the photo itself.
                    onError={event => {
                      const full = photo_4.signedUrl ?? photo_4.url;
                      if (full && event.currentTarget.src !== full) {
                        event.currentTarget.src = full;
                      }
                    }} />
                            {role && <span className="absolute inset-x-0 bottom-0 bg-primary text-center text-[10px] text-primary-foreground">
                                {role}
                              </span>}
                          </button>;
                })}
                    </div>}
                  {hasNextPage && <Button size="sm" variant="ghost" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
                      {isFetchingNextPage ? "Loading..." : "Show older photos"}
                    </Button>}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Background</Label>
                    {preparingCutouts && <RiLoader4Line className="size-4 animate-spin text-foreground-secondary" aria-label="Preparing Studio photos" />}
                  </div>
                  <PillSelect options={EXPORT_BACKDROPS} value={backdrop} onChange={setBackdrop} formatOption={option => STUDIO_BACKDROP_LABELS[option]} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Size</Label>
                    <PillSelect options={EXPORT_SIZES} value={size} onChange={setSize} formatOption={option_0 => SIZE_LABELS[option_0]} />
                  </div>
                  <div className="space-y-2">
                    <Label>Layout</Label>
                    <PillSelect options={EXPORT_LAYOUTS} value={layout} onChange={setLayout} formatOption={option_1 => LAYOUT_LABELS[option_1]} />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="marketing-before-label">Before label</Label>
                    <Input id="marketing-before-label" maxLength={24} value={labels.before} onChange={e => setLabels(l => ({
                  ...l,
                  before: e.target.value
                }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="marketing-after-label">After label</Label>
                    <Input id="marketing-after-label" maxLength={24} value={labels.after} onChange={e_0 => setLabels(l_0 => ({
                  ...l_0,
                  after: e_0.target.value
                }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="marketing-watermark">Watermark</Label>
                    <Input id="marketing-watermark" maxLength={80} placeholder="e.g. @yourclinic" value={effectiveWatermark} onChange={e_1 => setWatermarkText(e_1.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="marketing-disclaimer">Disclaimer</Label>
                    <Input id="marketing-disclaimer" maxLength={200} value={effectiveDisclaimer} onChange={e_2 => setDisclaimerText(e_2.target.value)} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="marketing-show-logo" checked={effectiveShowLogo} onCheckedChange={(checked: boolean) => setShowLogo(checked)} />
                  <Label htmlFor="marketing-show-logo">
                    Show the clinic logo
                  </Label>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <MarketingExportPreview beforeUrl={previewUrl(before)} afterUrl={previewUrl(after)} size={size} layout={layout} backdrop={backdrop} brandColor={brandColor} alignment={alignment} labels={labels} logoUrl={effectiveShowLogo ? tenant?.logoUrl ?? null : null} watermarkText={effectiveWatermark || null} disclaimerText={effectiveDisclaimer || null} />
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" disabled={!before || !after || aligning} onClick={() => void autoAlign()}>
                    {aligning ? <RiLoader4Line className="mr-1 size-4 animate-spin" /> : <RiFocus3Line className="mr-1 size-4" />}
                    <span>Auto-align</span>
                  </Button>
                  {alignment && <Button size="sm" variant="ghost" onClick={() => setFaces(null)}>
                      Undo align
                    </Button>}
                </div>
              </div>
            </div>}
        </DialogBody>
        {!result && <DialogFooter>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={!canExport} onClick={() => void runExport()}>
              {createExport.isPending && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
              <span>Create image</span>
            </Button>
          </DialogFooter>}
      </DialogContent>
    </Dialog>;
}